import CloseIcon from "@redotech/redo-icons/x-close.svg";
import classNames from "classnames";
import { memo, ReactNode } from "react";
import { Flex } from "../../flex";
import { Text } from "../../text";
import * as darkModeCss from "../../theme/component-colors-dark.module.css";
import { RedoButton, RedoButtonTheme } from "../buttons/redo-button";
import {
  RedoFeaturedIcon,
  RedoFeaturedIconProps,
} from "../featured-icon/redo-featured-icon";
import * as redoNotificationCss from "./redo-notification.module.css";

export interface NotificationButton {
  text: string;
  onClick(): void | Promise<void>;
  pending?: boolean;
  theme?: RedoButtonTheme;
}

export const notificationBreakpoint = ["mobile", "desktop"] as const;
export type NotificationBreakpoint = (typeof notificationBreakpoint)[number];

export interface NotificationProps {
  title?: string;
  description?: string;
  hideCloseX?: boolean;
  dismiss: () => void;
  primaryButton?: NotificationButton;
  secondaryButton?: NotificationButton;
  dismissButton?: Omit<NotificationButton, "onClick">;
  featuredIcon?: Omit<RedoFeaturedIconProps, "size">;
  breakpoint?: NotificationBreakpoint;
  children?: (dismiss: () => void) => ReactNode;
  fullModalImage?: string;
  dataTestId?: string;
  noPadding?: boolean;
  darkMode?: boolean;
  buttonAlignment?: "left" | "right";
}

/**
 * To actually show a notification, use the function {@link showNotification}
 */
export const RedoNotification = memo(function RedoNotification({
  title,
  description,
  primaryButton,
  secondaryButton,
  dismissButton,
  hideCloseX,
  dismiss,
  breakpoint = "desktop",
  featuredIcon,
  children = () => null,
  fullModalImage,
  dataTestId,
  noPadding = false,
  darkMode = false,
  buttonAlignment = "left",
}: NotificationProps) {
  return (
    <Flex
      bgColor="primary"
      borderColor="primary"
      borderRadius="lg"
      borderStyle="solid"
      borderWidth="1px"
      className={classNames(
        redoNotificationCss.container,
        darkMode && darkModeCss.darkMode,
      )}
      data-arbiter="RedoNotification"
      data-testid={dataTestId}
      dir={breakpoint === "desktop" ? "row" : "column"}
      gap={noPadding ? "none" : "xl"}
      p={noPadding ? "none" : "xl"}
    >
      {fullModalImage && (
        <Flex borderRadius="lg" className={redoNotificationCss.fullModalImage}>
          <img alt="Full modal image" src={fullModalImage} />
        </Flex>
      )}
      <Flex
        className={classNames(
          redoNotificationCss.contentContainer,
          breakpointToClass[breakpoint],
          fullModalImage ? redoNotificationCss.withImage : "",
        )}
      >
        {featuredIcon && (
          <Flex>
            <RedoFeaturedIcon {...featuredIcon} size="md" />
          </Flex>
        )}
        <Flex
          dir="column"
          gap="lg"
          grow={1}
          pr={
            breakpoint === "desktop" &&
            (dismissButton || primaryButton || !hideCloseX)
              ? "4xl"
              : "none"
          }
        >
          {title || description ? (
            <Flex dir="column" gap="xs">
              {title && (
                <Text fontSize="sm" fontWeight="semibold" textColor="primary">
                  {title}
                </Text>
              )}
              {description && (
                <Text fontSize="sm" fontWeight="regular" textColor="secondary">
                  {description}
                </Text>
              )}
            </Flex>
          ) : null}
          {children(dismiss)}
          {dismissButton || secondaryButton || primaryButton ? (
            <Flex
              gap="lg"
              justify={buttonAlignment === "right" ? "flex-end" : "flex-start"}
            >
              {dismissButton && (
                <RedoButton
                  hierarchy="secondary"
                  onClick={dismiss}
                  size="sm"
                  text={dismissButton.text}
                />
              )}
              {secondaryButton && (
                <RedoButton
                  hierarchy="secondary"
                  onClick={secondaryButton.onClick}
                  pending={secondaryButton.pending}
                  size="sm"
                  text={secondaryButton.text}
                  theme={secondaryButton.theme}
                />
              )}
              {primaryButton && (
                <RedoButton
                  hierarchy="primary"
                  onClick={async () => {
                    await primaryButton.onClick();
                    dismiss();
                  }}
                  pending={primaryButton.pending}
                  size="sm"
                  text={primaryButton.text}
                  theme={primaryButton.theme}
                />
              )}
            </Flex>
          ) : null}
        </Flex>
        {!hideCloseX && (
          <RedoButton
            aria-label="Dismiss"
            className={redoNotificationCss.closeButton}
            hierarchy="tertiary"
            IconLeading={CloseIcon}
            onClick={dismiss}
            size="md"
          />
        )}
      </Flex>
    </Flex>
  );
});

const breakpointToClass: Record<NotificationBreakpoint, string> = {
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  mobile: redoNotificationCss.mobile,
  // @ts-expect-error TODO: noUncheckedIndexedAccess FIXME
  desktop: redoNotificationCss.desktop,
};
